/**
 * Contador de jornada visible (BHM-CLD-REQ-044 §26) + reportero de contexto (§27).
 *  ● Jornada 01:12:34 · Trabajo identificado 00:58:20 · Pendiente 00:14:14
 *  ● Contabilizando · Enesca → Error WooCommerce 00:23:41
 * Estado = API (/activity/live). El cliente solo extrapola entre consultas (cada 30 s).
 * Privacidad: solo se envían módulo + ids; la inactividad solo detiene las señales (nunca marca "no productivo").
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { activityApi, errText, fmtHms, type BreakState, type LiveState } from '../../stores/activityApi';
import { workApi } from '../../stores/workApi';
import WorkdayTaskBar from './WorkdayTaskBar';
import { contextFromPath, useWorkContextStore, type WorkContextValue } from '../../stores/workContextStore';

const POLL_MS = 30_000;
const HEARTBEAT_MS = 60_000;
const DWELL_MS = 15_000;
const IDLE_STOP_MS = 10 * 60_000;

export const ACTIVITY_CHANGED = 'bhm:activity-changed';
export const notifyActivityChanged = () => { try { window.dispatchEvent(new Event(ACTIVITY_CHANGED)); } catch { /* sin window */ } };

/** Último movimiento del usuario (solo una marca de tiempo; nunca el contenido). */
function useLastInput() {
  const last = useRef(Date.now());
  useEffect(() => {
    const mark = () => { last.current = Date.now(); };
    const evs = ['pointerdown', 'keydown', 'scroll', 'pointermove', 'touchstart'] as const;
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark));
  }, []);
  return last;
}

/** Reportero: cambio de pantalla con dwell mínimo + latido mientras haya presencia y jornada fichada. */
export function useContextReporter(clockedIn: boolean, onResult: () => void) {
  const { pathname } = useLocation();
  const declared = useWorkContextStore((s) => s.value);
  const ctx: WorkContextValue = useMemo(() => {
    const base = contextFromPath(pathname);
    return declared ? { module: declared.module ?? base.module, projectId: declared.projectId ?? base.projectId, taskId: declared.taskId } : base;
  }, [pathname, declared]);
  const key = `${ctx.module ?? ''}|${ctx.projectId ?? ''}|${ctx.taskId ?? ''}`;
  const lastInput = useLastInput();
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const sent = useRef<string | null>(null);

  const send = useCallback(async () => {
    const c = ctxRef.current;
    try {
      const r = await activityApi.context({ module: c.module, projectId: c.projectId, taskId: c.taskId });
      if (r.state === 'OK') onResult();
    } catch { /* sin permiso o sin red: no se bloquea al usuario */ }
  }, [onResult]);

  useEffect(() => {
    if (!clockedIn) { sent.current = null; return; }
    const t = setTimeout(() => { sent.current = key; void send(); }, DWELL_MS);
    return () => clearTimeout(t);
  }, [key, clockedIn, send]);

  useEffect(() => {
    if (!clockedIn) return;
    const h = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInput.current > IDLE_STOP_MS) return; // sin presencia: se dejan de enviar señales (queda pendiente)
      if (!ctxRef.current.projectId) return;
      void send();
    }, HEARTBEAT_MS);
    return () => clearInterval(h);
  }, [clockedIn, send, lastInput]);
}

/**
 * Extrapolación local entre consultas: la jornada corre; corre como identificado solo si hay trabajo contabilizándose.
 * Con un descanso en curso (051A) corre el DESCANSO hasta agotar los 20 min registrables; el resto (y nunca el trabajo) va a pendiente.
 */
export function extrapolate(live: LiveState | null, fetchedAt: number, now: number) {
  if (!live) return null;
  const dt = live.clockedIn ? Math.max(0, Math.round((now - fetchedAt) / 1000)) : 0;
  const onBreak = !!live.clockedIn && !!live.onBreak;
  const breakBase = live.breakSec ?? 0;
  const breakLeft = Math.max(0, (live.breakEntitledSec ?? DAILY_BREAK_SEC) - breakBase);
  const breakTick = onBreak ? Math.min(dt, breakLeft) : 0;
  const rest = dt - breakTick; // tiempo que no es descanso
  const working = !onBreak && live.identifying;
  return {
    workday: live.workdaySec + dt,
    identified: live.identifiedSec + (working ? dt : 0),
    pending: live.pendingSec + (onBreak ? rest : working ? 0 : dt),
    breakSec: breakBase + breakTick,
    currentSec: live.current ? Math.max(0, Math.round((now - new Date(live.current.since).getTime()) / 1000)) : 0,
  };
}

/** Descanso diario definido: 20 min (≠ bloque de agenda de 120 min). */
export const DAILY_BREAK_SEC = 20 * 60;

export type DataState = 'REAL_DATA' | 'NO_DATA' | 'UNVERIFIED' | 'DISCONNECTED' | 'DEGRADED' | 'ERROR';

/** Estado del contador: lo que se muestra procede siempre del backend; sin datos se declara, no se simula. */
export function counterState(live: LiveState | null, failed: boolean): DataState {
  if (failed) return live ? 'DEGRADED' : 'DISCONNECTED';
  return live ? 'REAL_DATA' : 'NO_DATA';
}

/** Texto del descanso: sin registro no se afirma que se disfrutó; una API sin el dato lo marca como no verificado. */
export function breakText(live: LiveState | null, breakSec: number): { state: BreakState | 'UNVERIFIED'; text: string } {
  if (!live || live.breakState === undefined) return { state: 'UNVERIFIED', text: 'no verificado' };
  if (live.breakState === 'NOT_RECORDED') return { state: 'NOT_RECORDED', text: 'sin registrar' };
  return { state: live.breakState, text: `${fmtHms(breakSec)} / ${fmtHms(live.breakEntitledSec ?? DAILY_BREAK_SEC)}` };
}

export default function WorkdayCounter() {
  const [live, setLive] = useState<LiveState | null>(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try { const l = await activityApi.live(); setLive(l); setFetchedAt(Date.now()); setErr(null); } catch { setErr('sin conexión'); }
  }, []);

  useEffect(() => {
    void refresh();
    const p = setInterval(refresh, POLL_MS);
    const t = setInterval(() => setNow(Date.now()), 1000);
    const vis = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', vis);
    window.addEventListener(ACTIVITY_CHANGED, refresh);
    return () => { clearInterval(p); clearInterval(t); document.removeEventListener('visibilitychange', vis); window.removeEventListener(ACTIVITY_CHANGED, refresh); };
  }, [refresh]);

  useContextReporter(!!live?.clockedIn, refresh);

  const x = extrapolate(live, fetchedAt, now);
  const clockIn = async () => { setBusy(true); try { await workApi.clockIn(); await refresh(); } catch { setErr('no se pudo fichar'); } finally { setBusy(false); } };
  const toggleBreak = async () => {
    setBusy(true);
    try { if (live?.onBreak) await activityApi.endBreak(); else await activityApi.startBreak(); await refresh(); }
    catch (e) { setErr(errText(e, 'no se pudo registrar el descanso')); }
    finally { setBusy(false); }
  };

  if (!live) return err ? <span className="hub-desktop text-[11px] text-outline" data-testid="workday-error" data-state={counterState(null, true)}>Jornada: {counterState(null, true)} ({err})</span> : null;

  if (!live.clockedIn) {
    return (
      <div className="flex items-center gap-2 text-xs" data-testid="workday-idle">
        <span className="hub-desktop text-on-surface-variant">● Jornada sin iniciar</span>
        {live.autoClockOut && (
          <span className="hub-desktop text-[11px] text-[#F59E0B]" data-testid="wd-auto-clock-out">
            Jornada cerrada automáticamente a la hora prevista (fichaje de salida olvidado); {fmtHms(live.autoClockOut.excludedSec)} quedan excluidos de la jornada.
          </span>
        )}
        <button className="rounded-lg bg-[#FF5E1E] px-3 py-1 font-medium text-white hover:bg-[#FF5E1E]/90 disabled:opacity-50" disabled={busy} onClick={clockIn} data-testid="workday-clock-in">Fichar entrada</button>
      </div>
    );
  }

  const br = breakText(live, x!.breakSec);
  return (
    <div className="flex max-w-[calc(100vw-1.5rem)] flex-col items-start leading-tight" data-testid="workday-counter" data-state={counterState(live, !!err)} aria-live="off">
      <Link to="/jornada" className="flex items-center gap-1.5 text-[11px] text-on-surface hover:text-[#FF5E1E]" title="Ver el detalle de mi jornada">
        <span className="text-[#10B981]">●</span>
        <span>Jornada <b className="tabular-nums" data-testid="wd-workday">{fmtHms(x!.workday)}</b></span>
        <span className="hub-desktop text-on-surface-variant">· Trabajo identificado <b className="tabular-nums text-on-surface" data-testid="wd-identified">{fmtHms(x!.identified)}</b></span>
        <span className="hub-desktop text-on-surface-variant">· Pendiente <b className="tabular-nums text-on-surface" data-testid="wd-pending">{fmtHms(x!.pending)}</b></span>
        {br.state !== 'UNVERIFIED' && <span className="hub-desktop text-on-surface-variant">· Descanso <b className="tabular-nums text-on-surface" data-testid="wd-break" data-break-state={br.state}>{br.text}</b></span>}
      </Link>
      <div className="mt-0.5 flex max-w-full flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="wd-actions-row">
        {br.state !== 'UNVERIFIED' && (live.onBreak || br.state !== 'COMPLETE') && (
          <button className="rounded-lg border border-outline-variant px-2 py-0.5 text-[11px] text-on-surface-variant hover:text-on-surface disabled:opacity-50" disabled={busy} onClick={toggleBreak} data-testid="wd-break-toggle">
            {live.onBreak ? 'Terminar descanso' : 'Iniciar descanso (20 min)'}
          </button>
        )}
        <WorkdayTaskBar live={live} onChanged={refresh} />
      </div>
      {live.current ? (
        <span className="max-w-full truncate text-[11px] text-on-surface-variant sm:max-w-[34rem]" data-testid="wd-current" title={live.current.assigneeName ? `Responsable: ${live.current.assigneeName}` : undefined}>
          ● Contabilizando · {live.current.projectName}{live.current.processName ? ` · ${live.current.processName}` : ''}{live.current.taskTitle ? ` → ${live.current.taskCode ? live.current.taskCode + ' · ' : ''}${live.current.taskTitle}` : ''} <span className="tabular-nums">{fmtHms(x!.currentSec)}</span>
          {live.current.assigneeName ? <span className="hub-desktop"> · Resp: {live.current.assigneeName}</span> : null}
        </span>
      ) : (
        <span className="hub-desktop text-[11px] text-outline" data-testid="wd-current">Sin proyecto identificado: el tiempo queda pendiente de clasificar</span>
      )}
      {live.nexusRunning.length > 0 && <span className="hub-desktop text-[11px] text-[#60a5fa]" data-testid="wd-nexus">Nexus trabajando en {live.nexusRunning.length} {live.nexusRunning.length === 1 ? 'tarea' : 'tareas'} (no suma a tu jornada)</span>}
    </div>
  );
}
